import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, Users, DollarSign, FileText } from 'lucide-react';
import bookingService from '../services/bookingService';
import useCustomAlert from '../hooks/useCustomAlert';
import CustomAlert from './CustomAlert';

const AdminEditBookingModal = ({ show, onClose, booking, onSuccess }) => {
  const { alert, showAlert, closeAlert } = useCustomAlert();
  const [formData, setFormData] = useState({
    dataHora: '',
    numeroParticipantes: '',
    observacoes: '',
    preco: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (booking) {
      const dataHora = new Date(booking.dataHora);
      const localDateTime = new Date(dataHora.getTime() - dataHora.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);

      // Extrair valor numérico do preço
      const precoNumerico = booking.preco ? 
        parseFloat(booking.preco.replace('€', '').replace(',', '.').trim()) : '';

      setFormData({
        dataHora: localDateTime,
        numeroParticipantes: booking.numeroParticipantes || '',
        observacoes: booking.observacoes || '',
        preco: precoNumerico
      });
    }
  }, [booking]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {};
      
      // Só enviar campos que foram alterados
      if (formData.dataHora) {
        updateData.DataHora = new Date(formData.dataHora).toISOString();
      }
      if (formData.numeroParticipantes) {
        updateData.NumeroParticipantes = parseInt(formData.numeroParticipantes);
      }
      if (formData.observacoes !== booking.observacoes) {
        updateData.Observacoes = formData.observacoes;
      }
      if (formData.preco) {
        updateData.Preco = parseFloat(formData.preco);
      }

      await bookingService.adminUpdateBooking(booking.id, updateData);
      
      showAlert('Reserva atualizada com sucesso!', 'success');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (error) {
      showAlert(error.message || 'Erro ao atualizar reserva', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!show || !booking) return null;

  return (
    <>
      <CustomAlert alert={alert} onClose={closeAlert} />
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal admin-edit-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2 className="modal-title">Editar Reserva</h2>
            <button className="modal-close-btn" onClick={onClose}>
              <X size={24} />
            </button>
          </div>

          <div className="modal-body">
            <div className="booking-info-summary">
              <h3>{booking.servicoNome}</h3>
              <p className="booking-user">Cliente: {booking.user?.nome || booking.userName}</p>
              <p className="booking-tipo">Tipo: {booking.servicoTipo}</p>
            </div>

            <form onSubmit={handleSubmit} className="edit-booking-form">
              <div className="form-group">
                <label>
                  <Calendar size={18} />
                  Data e Hora
                </label>
                <input
                  type="datetime-local"
                  value={formData.dataHora}
                  onChange={(e) => setFormData({ ...formData, dataHora: e.target.value })}
                  min={new Date().toISOString().slice(0, 16)}
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  <Users size={18} />
                  Número de Participantes
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.numeroParticipantes}
                  onChange={(e) => setFormData({ ...formData, numeroParticipantes: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  <DollarSign size={18} />
                  Preço (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.preco}
                  onChange={(e) => setFormData({ ...formData, preco: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  <FileText size={18} />
                  Observações
                </label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  rows={4}
                  placeholder="Observações adicionais..."
                />
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-close" 
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : 'Guardar Alterações'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style jsx>{`
        .admin-edit-modal {
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .booking-info-summary {
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          padding: 1.5rem;
          border-radius: 0.75rem;
          margin-bottom: 2rem;
          border-left: 4px solid #2563eb;
        }

        .booking-info-summary h3 {
          margin: 0 0 0.5rem 0;
          color: #1e40af;
          font-size: 1.25rem;
        }

        .booking-info-summary p {
          margin: 0.25rem 0;
          color: #64748b;
          font-size: 0.95rem;
        }

        .booking-user {
          font-weight: 600;
          color: #334155;
        }

        .edit-booking-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          color: #334155;
          font-size: 0.95rem;
        }

        .form-group input,
        .form-group textarea {
          padding: 0.75rem;
          border: 2px solid #e2e8f0;
          border-radius: 0.5rem;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .form-group textarea {
          resize: vertical;
          font-family: inherit;
        }

        .modal-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 2px solid #e2e8f0;
        }

        .btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 0.5rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 1rem;
        }

        .btn-close {
          background: #f1f5f9;
          color: #64748b;
        }

        .btn-close:hover:not(:disabled) {
          background: #e2e8f0;
        }

        .btn-primary {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
};

export default AdminEditBookingModal;